import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Heart, MessageCircleQuestion, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr, imgSrc } from "@/lib/apiClient";
import { Loading } from "@/components/shared";
import { TrustBadge } from "@/components/ReferralFeedback";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useBrowseLocation } from "@/hooks/useBrowseLocation";

function timeAgo(iso) {
  if (!iso) return "";
  const sec = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

export function RequestsList() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const { searchParams } = useBrowseLocation({ enabled: true });

  const load = () => {
    api.get("/requests", { params: searchParams }).then(({ data }) => setItems(data)).catch((e) => {
      toast.error(apiErr(e));
      setItems([]);
    });
  };
  useEffect(() => { load(); }, [searchParams.lat, searchParams.lng]);

  if (items === null) return <Loading />;

  const open = items.filter((r) => r.status === "open");
  const closed = items.filter((r) => r.status !== "open");

  return (
    <div className="mx-auto max-w-2xl px-4 py-6" data-testid="requests-list">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black tracking-tight">Requests</h1>
          <p className="text-sm text-muted-foreground">Looking for someone? Ask the community.</p>
        </div>
        <Button className="bg-brand-green hover:bg-brand-green-dark" onClick={() => nav("/app/requests/new")} data-testid="ask-request-btn">
          <Plus className="mr-1 h-4 w-4" /> Ask
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="mt-10 rounded-[14px] border border-dashed border-border bg-card p-8 text-center" data-testid="requests-empty">
          <MessageCircleQuestion className="mx-auto h-10 w-10 text-brand-green/60" />
          <p className="mt-3 font-semibold">No one&apos;s asking yet nearby</p>
          <p className="mt-1 text-sm text-muted-foreground">Be the first — neighbors love helping with a referral.</p>
          <Button className="mt-4 bg-brand-gold text-brand-ink hover:bg-brand-gold-dark" onClick={() => nav("/app/requests/new")}>
            Post a question
          </Button>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {open.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => nav(`/app/requests/${r.id}`)}
              className="w-full rounded-[14px] border border-border bg-card p-4 text-left hover:border-brand-green/40"
              data-testid={`request-card-${r.id}`}
            >
              <span className="rounded-full bg-brand-green/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-green">Looking for</span>
              <p className="mt-2 text-base font-semibold">{r.question}</p>
              <p className="mt-2 text-xs text-muted-foreground">
                {r.asker?.first_name} {r.asker?.initial}.
                {r.area?.label ? ` · ${r.area.label}` : ""}
                {r.distance_mi != null ? ` · ${r.distance_mi} mi` : ""}
                {" · "}{timeAgo(r.created_at)}
                {" · "}{r.answer_count} answer{r.answer_count === 1 ? "" : "s"} →
              </p>
            </button>
          ))}
          {closed.length > 0 ? (
            <>
              <p className="pt-4 text-xs font-bold uppercase tracking-wide text-muted-foreground">Answered</p>
              {closed.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => nav(`/app/requests/${r.id}`)}
                  className="w-full rounded-[14px] border border-border/60 bg-secondary/30 p-4 text-left opacity-80"
                  data-testid={`request-card-${r.id}`}
                >
                  <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Answered</span>
                  <p className="mt-1 font-medium">{r.question}</p>
                </button>
              ))}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function AskRequest() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { coords, areaInput, setAreaInput, setArea, savingArea } = useBrowseLocation({ enabled: true });
  const [question, setQuestion] = useState(params.get("q") || "");
  const [busy, setBusy] = useState(false);

  const post = async () => {
    setBusy(true);
    try {
      const area = {
        latitude: coords?.lat ?? null,
        longitude: coords?.lng ?? null,
        label: areaInput.trim() || null,
      };
      if (areaInput.trim() && !coords) {
        await setArea(areaInput.trim());
      }
      const { data } = await api.post("/requests", { question, area });
      toast.success("Posted");
      nav(`/app/requests/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg px-4 py-6" data-testid="ask-request-form">
      <h1 className="text-2xl font-black tracking-tight">Ask the community</h1>
      <p className="mt-1 text-sm text-muted-foreground">One question. Neighbors can attach a business referral.</p>
      <Textarea
        className="mt-4"
        rows={3}
        maxLength={200}
        placeholder="Who does concrete pools?"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        data-testid="ask-question-input"
      />
      <p className="mt-1 text-xs text-muted-foreground">{question.length}/200</p>
      <label className="mt-4 block text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Area</label>
      <Input
        className="mt-1"
        placeholder="City or ZIP (optional)"
        value={areaInput}
        onChange={(e) => setAreaInput(e.target.value)}
        data-testid="ask-area-input"
      />
      <Button
        className="mt-6 w-full bg-brand-green hover:bg-brand-green-dark"
        disabled={busy || !question.trim() || savingArea}
        onClick={post}
        data-testid="post-request-btn"
      >
        {busy ? "Posting…" : "Post my question"}
      </Button>
    </div>
  );
}

export function RequestDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [branchQ, setBranchQ] = useState("");
  const [branchHits, setBranchHits] = useState([]);
  const [attached, setAttached] = useState(null);
  const [busy, setBusy] = useState(false);
  const { searchParams } = useBrowseLocation({ enabled: true });

  const load = () => {
    api.get(`/requests/${id}`).then(({ data: d }) => setData(d)).catch(() => setData(false));
  };
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!branchQ.trim()) { setBranchHits([]); return; }
    const t = setTimeout(() => {
      api.get("/branches/search", { params: { q: branchQ, ...searchParams } })
        .then(({ data: rows }) => setBranchHits(rows.slice(0, 6)))
        .catch(() => setBranchHits([]));
    }, 250);
    return () => clearTimeout(t);
  }, [branchQ, searchParams.lat, searchParams.lng]);

  const like = async (answerId) => {
    try {
      await api.post(`/answers/${answerId}/like`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openReferral = async (answerId) => {
    try {
      const { data: res } = await api.post(`/answers/${answerId}/use`);
      nav(res.share_url || `/r/${res.code}`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const close = async () => {
    if (!window.confirm("Mark this request as answered?")) return;
    try {
      await api.post(`/requests/${id}/close`);
      toast.success("Closed");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const submitAnswer = async () => {
    if (!body.trim() && !attached) {
      toast.error("Add a note or attach a business");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/requests/${id}/answers`, {
        body: body.trim() || null,
        branch_id: attached?.id || null,
      });
      setBody("");
      setAttached(null);
      setBranchQ("");
      toast.success("Answer posted");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (data === null) return <Loading />;
  if (data === false) return <p className="p-8 text-center text-muted-foreground">Request not found.</p>;

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 pb-28" data-testid="request-detail">
      <button type="button" className="text-sm text-brand-green" onClick={() => nav("/app/requests")}>← Requests</button>
      <div className="mt-3 rounded-[14px] border border-border bg-card p-5">
        <span className="rounded-full bg-brand-green/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-green">
          {data.status === "open" ? "Looking for" : "Answered"}
        </span>
        <h1 className="mt-2 text-xl font-black tracking-tight" data-testid="request-question">{data.question}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {data.asker?.first_name} {data.asker?.initial}.
          {data.area?.label ? ` · ${data.area.label}` : ""}
          {" · "}{timeAgo(data.created_at)}
        </p>
        {data.is_asker && data.status === "open" ? (
          <Button variant="outline" size="sm" className="mt-3" onClick={close} data-testid="close-request-btn">Mark answered</Button>
        ) : null}
      </div>

      <h2 className="mt-6 text-sm font-bold uppercase tracking-wide text-muted-foreground">Answers</h2>
      <div className="mt-3 space-y-3">
        {(data.answers || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No answers yet — be the first to help.</p>
        ) : (data.answers || []).map((a) => (
          <div
            key={a.id}
            className={`rounded-[14px] border p-4 ${a.branch ? "border-brand-green/30 bg-card shadow-sm" : "border-border bg-secondary/20"}`}
            data-testid={`answer-${a.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-green/15 font-bold text-brand-green">
                  {a.answerer?.initial || "?"}
                </span>
                {a.answerer?.first_name}
                {a.is_owner_answer ? (
                  <span className="rounded-full bg-brand-gold/20 px-2 py-0.5 text-[10px] font-bold text-brand-gold-dark">Owner</span>
                ) : (
                  <TrustBadge badge={a.answerer?.trust_badge} />
                )}
                {a.added_as_new ? (
                  <span className="rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] font-bold text-blue-700">Added as new</span>
                ) : null}
              </div>
              <button type="button" onClick={() => like(a.id)} className="flex items-center gap-1 text-sm" data-testid={`like-answer-${a.id}`}>
                <Heart className={`h-4 w-4 ${a.liked_by_me ? "fill-red-500 text-red-500" : "text-muted-foreground"}`} />
                {a.like_count || 0}
              </button>
            </div>
            {a.body ? <p className="mt-2 text-sm">{a.body}</p> : null}
            {a.branch ? (
              <div className="mt-3 flex gap-3 rounded-[12px] border border-border bg-background p-3">
                {a.branch.logo_url ? (
                  <img src={imgSrc(a.branch.logo_url)} alt="" className="h-12 w-12 rounded-[10px] object-cover" />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-[10px] bg-brand-green/10 text-lg font-black text-brand-green">
                    {a.branch.company_name?.[0]}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold">{a.branch.company_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {a.branch.subcategory_badge || a.branch.city || a.branch.address || ""}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => nav(`/b/${a.branch.id}`)} data-testid={`see-business-${a.id}`}>
                      See business
                    </Button>
                    {data.is_asker && a.share_url ? (
                      <Button size="sm" className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark" onClick={() => openReferral(a.id)} data-testid={`use-referral-${a.id}`}>
                        Use this referral
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {data.status === "open" ? (
        <div className="fixed bottom-0 left-0 right-0 border-t border-border bg-background/95 p-4 backdrop-blur">
          <div className="mx-auto max-w-2xl space-y-2">
            {attached ? (
              <div className="flex items-center justify-between rounded-[10px] bg-brand-green/10 px-3 py-2 text-sm">
                <span>Attached: <strong>{attached.company_name}</strong></span>
                <button type="button" className="text-muted-foreground" onClick={() => setAttached(null)}>Remove</button>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center gap-2 rounded-[10px] border border-border px-2">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <input
                    className="flex-1 bg-transparent py-2 text-sm outline-none"
                    placeholder="Search a business to attach…"
                    value={branchQ}
                    onChange={(e) => setBranchQ(e.target.value)}
                    data-testid="answer-branch-search"
                  />
                </div>
                {branchHits.length > 0 ? (
                  <div className="absolute bottom-full mb-1 w-full rounded-[10px] border border-border bg-card shadow-lg">
                    {branchHits.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-secondary"
                        onClick={() => { setAttached(b); setBranchQ(""); setBranchHits([]); }}
                      >
                        {b.company_name}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="block w-full border-t border-border px-3 py-2 text-left text-sm text-brand-green"
                      onClick={() => nav(`/app/add-business?name=${encodeURIComponent(branchQ)}&returnRequest=${id}`)}
                    >
                      Can&apos;t find them? Add {branchQ || "a business"}
                    </button>
                  </div>
                ) : branchQ.trim() ? (
                  <div className="absolute bottom-full mb-1 w-full rounded-[10px] border border-border bg-card p-2 shadow-lg">
                    <button
                      type="button"
                      className="text-sm text-brand-green"
                      onClick={() => nav(`/app/add-business?name=${encodeURIComponent(branchQ)}&returnRequest=${id}`)}
                    >
                      No one found — add {branchQ} then attach
                    </button>
                  </div>
                ) : null}
              </div>
            )}
            <div className="flex gap-2">
              <Input
                placeholder="Optional note"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                data-testid="answer-body-input"
              />
              <Button className="bg-brand-green hover:bg-brand-green-dark" disabled={busy} onClick={submitAnswer} data-testid="post-answer-btn">
                Reply
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
